import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthContext } from '../AuthContext';
import { fetchCounts } from './inboxApi';

// DJB-264: the open ("to do") request counts from GET /webforms/counts, shared
// between the nav badges (Inbox / Contact Requests / Song Requests) and the
// inbox pages' To Do counter. Loaded once the user is signed in; the inbox
// pages call refreshCounts after every complete/delete so the badges follow.
export const EMPTY_COUNTS = { contactOpen: 0, songsOpen: 0 };

export const InboxCountsContext = createContext(null);

const normalize = (c) => ({
  contactOpen: Number(c?.contactOpen) || 0,
  songsOpen: Number(c?.songsOpen) || 0,
});

export const InboxCountsProvider = ({ children }) => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const latest = useRef(0);

  const refreshCounts = useCallback(() => {
    if (!accessToken) {
      return Promise.resolve();
    }
    const requestId = ++latest.current;
    return fetchCounts(accessToken)
      .then((c) => {
        // Ignore a slow response that a newer refresh has overtaken
        if (requestId === latest.current) {
          setCounts(normalize(c));
        }
      })
      .catch((error) => console.error('Failed to fetch inbox counts:', error));
  }, [accessToken]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const value = useMemo(() => ({ counts, refreshCounts }), [counts, refreshCounts]);

  return <InboxCountsContext.Provider value={value}>{children}</InboxCountsContext.Provider>;
};

// Stable fallback so consumers rendered without a provider (standalone tests)
// get zero badges and a no-op refresh instead of re-running effects forever
const NO_PROVIDER = { counts: EMPTY_COUNTS, refreshCounts: () => Promise.resolve() };

export const useInboxCounts = () => useContext(InboxCountsContext) || NO_PROVIDER;

export default InboxCountsProvider;
